<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="orderTitle"
      [subtitle]="propertySubtitle"
      [canGoBack]="!hideBackButton"
      [customBack]="'restocking'"
      [showDropdownMenu]="orderDropdownMenu.length > 0"
      [dropdownMenu]="orderDropdownMenu" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  @if (loaded && !notFound) {
    <div class="restock-sticky-header">
      <tidy-wrapper>
        @if (showBreakdown) {
          <tidy-row [alignLastItemRight]="true" class="vertical-align-center">
            <tidy-text [body]="'Subtotal'" />
            <tidy-text [body]="subtotalText" />
          </tidy-row>
          <tidy-row [alignLastItemRight]="true" class="vertical-align-center">
            <tidy-text [body]="'Tax'" />
            <tidy-text [body]="taxText" />
          </tidy-row>
          <tidy-row [alignLastItemRight]="true" class="vertical-align-center">
            <tidy-text [title]="'Total'" />
            <tidy-text
              [title]="finalTotalText"
              class="restock-price" />
          </tidy-row>
        } @else {
          <tidy-row [alignLastItemRight]="true" class="vertical-align-center">
            <tidy-text
              [title]="'Total'" />
            @if (showPrices && finalTotalText) {
              <tidy-text
                [title]="finalTotalText"
                class="restock-price" />
            } @else {
              <tidy-text
                [helper]="'Updated once ordered'" />
            }
          </tidy-row>
        }
      </tidy-wrapper>
    </div>
  } @else if (!loaded) {
    <!-- Real labels over grey values while the order loads. -->
    <div class="restock-sticky-header restock-skeleton">
      <tidy-wrapper>
        <div class="restock-line-row">
          <tidy-text
            [title]="'Total'" />
          <span class="restock-spacer"></span>
          <ion-skeleton-text [animated]="true" class="restock-skel-text-sm" />
        </div>
      </tidy-wrapper>
    </div>
  }

  <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">

    @if (loaded) {
      @if (notFound) {
        <tidy-alert>
          <tidy-row>
            <tidy-text
              [body]="'This restock order could not be found.'" />
          </tidy-row>
        </tidy-alert>
      } @else {
        <tidy-alert class="black-background">
          <tidy-row class="extra-bottom-padding">
            <tidy-text
              [header]="'Order Status'" />
          </tidy-row>
          <!-- Same horizontal stepper the item page's Current Order card uses:
               pulsing dot on the current step, accent connectors behind completed
               ones, muted grey ahead. The step's own detail/timestamp follows below
               so nothing from the old vertical timeline is lost. -->
          <div class="restock-item-stepper">
            @for (step of timelineSteps; track step.label) {
              <div [class]="step.stepClass">
                <div class="restock-item-step-dot"></div>
                <div class="restock-item-step-label">{{ step.label | translate }}</div>
                @if (step.timeText) {
                  <div class="restock-item-step-time">{{ step.timeText }}</div>
                }
              </div>
            }
          </div>
          @if (currentStepDetail) {
            <tidy-row class="vertical-align-top">
              <tidy-text
                [helper]="currentStepDetail" />
            </tidy-row>
          }
          @if (orderNumberText) {
            <tidy-row class="vertical-align-top">
              <tidy-text
                [helper]="orderNumberText"
                [translate]="false" />
            </tidy-row>
          }
          @if (deliveryText) {
            <tidy-row class="vertical-align-top">
              <tidy-text
                [helper]="'Delivery: ' + deliveryText"
                [translate]="false" />
            </tidy-row>
          }
        </tidy-alert>

        <!-- Attach this order to a job so a pro restocks it there (card gCUZuCjf). -->
        <restock-assign-job
          [order]="orderForAssign" />

        <tidy-row>
          <tidy-text
            [header]="'Items'" />
        </tidy-row>
        @for (item of itemRows; track item.roomObjectId) {
          <tidy-card
            class="restock-order-item-card"
            (click)="goToItem(item)">
            <div class="restock-item-preview">
              <div class="restock-item-thumb">
                @if (item.photoUrl) {
                  <img
                    [src]="item.photoUrl"
                    class="restock-item-thumb-photo"
                    alt="" />
                } @else {
                  <tidy-image
                    [src]="item.icon"
                    class="restock-item-thumb-icon" />
                }
              </div>
              <div class="restock-item-body">
                <div class="restock-item-title-row">
                  <tidy-text
                    [body]="item.name"
                    [translate]="false"
                    class="bold" />
                  <div class="restock-item-trailing">
                    @if (showPrices) {
                      @if (item.oldLineTotalText) {
                        <tidy-text
                          [body]="item.oldLineTotalText"
                          class="restock-price-struck" />
                      }
                      <tidy-text
                        [body]="item.lineTotalText"
                        class="restock-price" />
                    }
                    <tidy-image
                      [src]="'assets/img/chevron-right.svg'"
                      class="body-size restock-item-chevron" />
                  </div>
                </div>
                @if (item.modelText) {
                  <div class="restock-item-model">
                    <tidy-text
                      [helper]="item.modelText"
                      [translate]="false" />
                  </div>
                }
                <div class="restock-item-meta">
                  @if (item.levelLabel) {
                    <span [class]="item.levelClass">{{ item.levelLabel | translate }}</span>
                  }
                  <tidy-text
                    [helper]="item.qtyText" />
                </div>
                @if (item.stockText) {
                  <div class="restock-line-row">
                    <tidy-image
                      [src]="'assets/svg/cube-outline.svg'"
                      class="body-size restock-item-meta-icon" />
                    <tidy-text
                      [helper]="item.stockText"
                      [translate]="false" />
                  </div>
                }
                @if (item.productLink) {
                  <div
                    class="restock-product-preview restock-icon-action"
                    (click)="goToProduct(item.productLink, $event)">
                    <div class="restock-product-thumb">
                      @if (item.photoUrl) {
                        <img
                          [src]="item.photoUrl"
                          class="restock-product-thumb-photo"
                          alt="" />
                      } @else {
                        <tidy-image
                          [src]="item.icon"
                          class="restock-product-thumb-icon" />
                      }
                      <tidy-image
                        [src]="'assets/svg/walmart-spark.svg'"
                        class="restock-product-badge" />
                    </div>
                    <div class="restock-product-info">
                      <tidy-text
                        [body]="item.productTitle"
                        [translate]="false"
                        class="restock-product-title" />
                      @if (item.productPriceLabel) {
                        <tidy-text
                          [helper]="item.productPriceLabel"
                          [translate]="false"
                          class="restock-product-price" />
                      }
                      <tidy-text
                        [smallHelper]="item.productLink"
                        [translate]="false"
                        class="restock-product-url" />
                    </div>
                    <tidy-image
                      [src]="'assets/svg/open-outline.svg'"
                      class="body-size restock-product-external" />
                  </div>
                } @else if (item.resolvedText) {
                  <div class="restock-line-row">
                    <tidy-text
                      [helper]="item.resolvedText"
                      [translate]="false" />
                  </div>
                }
                @if (item.hasBackups) {
                  <div
                    class="restock-line-row restock-icon-action"
                    (click)="toggleItemBackups(item, $event)">
                    <tidy-image
                      [src]="item.showBackups ? 'assets/svg/chevron-up-outline.svg' : 'assets/svg/chevron-down-outline.svg'"
                      class="body-size" />
                    <tidy-text
                      [helper]="item.showBackups ? 'Hide backups' : item.backupsShowText"
                      [translate]="false" />
                  </div>
                }
                @if (item.substitutionNote) {
                  <tidy-alert class="restock-substitution-alert">
                    <tidy-row class="vertical-align-center">
                      <tidy-image
                        [src]="'assets/svg/alert-circle-outline.svg'"
                        class="body-size" />
                      <tidy-text
                        [body]="item.substitutionNote" />
                    </tidy-row>
                  </tidy-alert>
                }
                @if (item.showBackups) {
                  @for (backup of item.backups; track backup.text) {
                    <div class="restock-line-row restock-indent">
                      <tidy-text
                        [helper]="backup.text"
                        [translate]="false" />
                    </div>
                  }
                }
              </div>
            </div>
          </tidy-card>
        }

      }
    } @else {
      <!-- Loading skeleton: real section headers over grey placeholders that
           mirror the horizontal stepper and the item cards, so the data swaps in
           without shifting. -->
      <div class="restock-skeleton">
        <tidy-alert class="black-background">
          <tidy-row class="extra-bottom-padding">
            <tidy-text
              [header]="'Order Status'" />
          </tidy-row>
          <div class="restock-item-stepper">
            @for (step of skeletonSteps; track step) {
              <div class="restock-item-step">
                <div class="restock-item-step-dot"></div>
                <ion-skeleton-text [animated]="true" class="item-skel-step-label" />
              </div>
            }
          </div>
          <div class="restock-line-row">
            <ion-skeleton-text [animated]="true" class="restock-skel-text" />
          </div>
        </tidy-alert>

        <tidy-row>
          <tidy-text
            [header]="'Items'" />
        </tidy-row>
        @for (row of skeletonRows; track row) {
          <tidy-card>
            <div class="restock-line-row">
              <ion-skeleton-text [animated]="true" class="restock-skel-icon" />
              <ion-skeleton-text [animated]="true" class="restock-skel-name" />
              <span class="restock-spacer"></span>
              <ion-skeleton-text [animated]="true" class="restock-skel-price" />
            </div>
            <div class="restock-line-row">
              <ion-skeleton-text [animated]="true" class="restock-skel-text" />
            </div>
          </tidy-card>
        }
      </div>
    }

  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
